import { QueryType, YesNoNARadioOptions, YesNoRadioOptions } from "../../../models";
import { Input, RadioGroup, RadioOption, Select } from "../../../component-library";
import { capitalizeFirst, displayDate, displayDateAndTime } from "../../../helper-functions";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import { RenderedFormProps } from "../RenderForm.tsx";
import {
    useShipmentQuery,
    useSurgeryQuery,
    useTerritoryQuery,
    useTissueQuery,
    useUserBasicQuery,
} from "../../../queries";

const yesNoOptions = YesNoRadioOptions.map<RadioOption>((o) => ({ ...o, value: capitalizeFirst(o.value) }));
const yesNoNAOptions = YesNoNARadioOptions.map<RadioOption>((o) => ({ ...o, value: capitalizeFirst(o.value) }));

export const TissueOmarReturned = ({ formState }: Readonly<RenderedFormProps>) => {
    const {
        listQuery: { data: territories },
    } = useTerritoryQuery();

    const territoryOptions = territories?.map((t) => ({ label: t.name, value: t.id })) ?? [];

    // API logic relies on these values; see FormDataService.Sign()
    const acceptableUnacceptableOptions = ["Acceptable", "Unacceptable"].map((o) => ({ label: o, value: o }));

    const {
        detailQuery: { data: tissue },
    } = useTissueQuery({ queryType: QueryType.Detail, id: formState.formData?.tissueId });

    const {
        detailQuery: { data: returnShipment },
    } = useShipmentQuery({ queryType: QueryType.Detail, id: formState.formData?.shipmentId });

    const {
        detailQuery: { data: user },
    } = useUserBasicQuery({ queryType: QueryType.Detail, id: returnShipment?.packedByUserId });

    const {
        detailQuery: { data: surgery },
    } = useSurgeryQuery({ queryType: QueryType.Detail, id: formState.formData?.surgeryId });

    return (
        <Container className="p-4">
            <div className="text-center mb-4">
                <h1>Ocular Management Record - Return</h1>
            </div>
            <div className="mb-4">
                <Row>
                    <Col>Tissue Number: {tissue?.number}</Col>
                    <Col>Return Initiated by: {user?.name}</Col>
                    <Col>{displayDateAndTime(returnShipment?.shippedOn)}</Col>
                </Row>
            </div>

            <Form className="mb-4">
                <h2>Notification of Return</h2>
                <Row className="mb-4">
                    <Col>
                        <Input inlineLabel accessor="callersName" label="Caller's Name:" />
                    </Col>
                    <Col>Surgical Facility: {surgery?.locationAccount?.name}</Col>
                </Row>

                <Row className="mb-4">
                    <Col>Surgeon: {`${surgery?.surgeon?.firstName + " " + surgery?.surgeon?.lastName}`}</Col>
                    <Col>Intended Date of Surgery: {displayDate(surgery?.surgeryDate)}</Col>
                </Row>
                <Row className="mb-4">
                    <Col>Patient's Name: {`${surgery?.recipientFirstName + " " + surgery?.recipientLastName}`}</Col>
                    <Col>
                        <Input inlineLabel accessor="reasonForNotUsingTissue" label="Reason for not using the tissue" />
                    </Col>
                </Row>
            </Form>

            <Form className="mb-4">
                <h2>Storage Conditions at Facility</h2>
                <RadioGroup
                    inline
                    options={yesNoNAOptions}
                    accessor="tissueRefrigerated"
                    inlineGroupName
                    groupName="The tissue was kept refrigerated between (2°C-8°C):"
                />
                <Row>
                    <Col sm="auto" className="ps-0">
                        <Input
                            inlineLabel
                            type="number"
                            accessor="nonRefrigeratedTime"
                            label="If No, how long was the tissue non-refrigerated?"
                            append="Hours"
                        />
                    </Col>
                </Row>
                <RadioGroup
                    inline
                    options={yesNoOptions}
                    accessor="sealIntact"
                    inlineGroupName
                    groupName="Is the tamper-evident seal intact?"
                />
            </Form>

            <Form className="mb-4">
                <h2>Disposition of Tissue</h2>
                <Col>
                    <Select
                        inlineLabel
                        accessor="returnedTissueTerritoryId"
                        options={territoryOptions}
                        label="Region Tissue returned to:"
                        isDisabled={formState.isReadonly}
                    />
                </Col>
                <Input
                    inlineLabel
                    accessor="tissueStoredLocation"
                    label="Location where the tissue will be stored until pick-up:"
                />
                <Row>
                    <Col className="ps-0">
                        <Input inlineLabel accessor="transportation" label="Transportation / Shipping Arrangements:" />
                    </Col>
                    <Col className="pe-0">
                        <Input inlineLabel accessor="trackingNumber" label="Tracking Number:" />
                    </Col>
                </Row>
            </Form>

            <Table bordered>
                <thead>
                    <tr>
                        <th className="col-6">Assessment of Incoming Tissue</th>
                        <th className="text-center">OD</th>
                        <th className="text-center">OS</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="col-6">
                            Tissue number on label is consistent with Donor information/Return information
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueNumberConsistentWithDonorInfoOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueNumberConsistentWithDonorInfoOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Indication of wet-ice upon arrival</td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="indicationOfWetIceUponArrivalOD" />
                        </td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="indicationOfWetIceUponArrivalOS" />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Storage solution acceptable (if Optisol, color range acceptable)</td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="preservationMediumAcceptableOD" />
                        </td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="preservationMediumAcceptableOS" />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Shipping container: Secured and absent of leaks.</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="shippingContainerSecuredAndAbsentOfLeaksOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="shippingContainerSecuredAndAbsentOfLeaksOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">
                            Tissue storage container: Absent of leaks and tamper-evident seals intact (if applicable)
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueStorageContainerAbsentOfLeaksAndTamperEvidentSealsIntactOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueStorageContainerAbsentOfLeaksAndTamperEvidentSealsIntactOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Tissue was transported under Quarantine (if applicable)</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueWasTransportedUnderQuarantineOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueWasTransportedUnderQuarantineOS"
                            />
                        </td>
                    </tr>
                </tbody>
            </Table>

            <Form className="mb-4">
                <h2>Assessment and Review of Returned Tissue</h2>
                {/* API logic relies on this accessor */}
                <Select
                    inlineLabel
                    accessor="acceptableUnacceptable"
                    options={acceptableUnacceptableOptions}
                    label="Acceptable/Unacceptable"
                    isDisabled={formState.isReadonly}
                />
            </Form>
        </Container>
    );
};
